import React, { useEffect, useState } from "react";
import { Save, SlidersHorizontal, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { VITAL_META } from "@/pages/rpm/constants";

const FIELDS = [
  ["lo_critical", "Critical Low (≤)"],
  ["lo_moderate", "Moderate Low (≤)"],
  ["normal_min",  "Normal Min"],
  ["normal_max",  "Normal Max"],
  ["hi_moderate", "Moderate High (≤)"],
  ["hi_critical", "Critical High (>)"],
];

const VITALS = ["bp_systolic", "bp_diastolic", "heart_rate", "spo2", "temperature", "respiratory_rate"];

export default function RpmThresholds() {
  const [defaults, setDefaults] = useState({});
  const [globals, setGlobals] = useState({}); // vital_type → row
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get("/rpm/thresholds");
      // The seeded band defaults live under `meta`, not at the response root
      // (RpmThresholdController::index). Reading r.data.defaults gave undefined,
      // and the spread below then threw on the first vital.
      const apiDefaults = r.data.meta?.defaults ?? {};
      setDefaults(apiDefaults);
      const byVt = {};
      for (const t of r.data.data ?? []) {
        if (t.scope === "global") byVt[t.vital_type] = t;
      }
      // Fill missing with defaults so UI always shows full form
      const merged = {};
      for (const vt of VITALS) {
        const base = apiDefaults[vt] ?? {};
        merged[vt] = byVt[vt] ? { ...base, ...byVt[vt] } : { ...base, vital_type: vt, scope: "global" };
      }
      setGlobals(merged);
      setDirty({});
    } catch (e) { toast.error(formatApiError(e)); }
  }

  useEffect(() => { load(); }, []);

  function update(vt, key, value) {
    setGlobals((g) => ({ ...g, [vt]: { ...g[vt], [key]: value === "" ? null : parseFloat(value) } }));
    setDirty((d) => ({ ...d, [vt]: true }));
  }

  async function saveOne(vt) {
    setSaving(true);
    try {
      const row = globals[vt];
      await api.put("/rpm/thresholds", {
        scope: "global", scope_value: null, vital_type: vt,
        lo_critical: row.lo_critical, lo_moderate: row.lo_moderate,
        normal_min: row.normal_min, normal_max: row.normal_max,
        hi_moderate: row.hi_moderate, hi_critical: row.hi_critical,
      });
      toast.success(`${VITAL_META[vt].label} thresholds saved`);
      setDirty((d) => ({ ...d, [vt]: false }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  function resetOne(vt) {
    setGlobals((g) => ({ ...g, [vt]: { ...defaults[vt], vital_type: vt, scope: "global" } }));
    setDirty((d) => ({ ...d, [vt]: true }));
  }

  return (
    <div data-testid="rpm-thresholds-page">
      <PageHeader title="RPM Vital Thresholds"
        subtitle="Global ranges that drive threshold-based alerts and the vital tile colors on the Command Center." />

      <div className="space-y-4">
        {VITALS.map((vt) => (
          <div key={vt} className="hah-card p-5" data-testid={`thresholds-${vt}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-[#0B7E8C]" />
                <h3 className="text-base font-semibold text-[#1F2937]">{VITAL_META[vt].label}</h3>
                <span className="text-xs text-[#94A3B8]">({VITAL_META[vt].unit})</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => resetOne(vt)} className="border-[#E5E7EB]">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default
                </Button>
                <Button size="sm" onClick={() => saveOne(vt)} disabled={saving || !dirty[vt]}
                  className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`thresholds-save-${vt}`}>
                  <Save className="w-3.5 h-3.5 mr-1" /> Save
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</label>
                  <Input type="number" step="0.1"
                    value={globals[vt]?.[k] ?? ""}
                    onChange={(e) => update(vt, k, e.target.value)}
                    data-testid={`thresholds-${vt}-${k}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
